import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import DealTemplateModal from "./modals/DealTemplateModal";
import AthleteOfferModal from "./modals/AthleteOfferModal";
import {
  ARCHETYPE_DOT,
  ARCHETYPE_DOT_FALLBACK,
  EMPTY_VALUE,
  OFFER_RANGE,
  PLATFORMS,
  deliverableCount,
  fmtCompact,
  fmtMoney,
  fmtMoneyCompact,
  fmtRange,
  fullName,
  offerStatus,
} from "./brandUtils";
import {
  Avatar,
  Badge,
  Btn,
  Card,
  InsightBox,
  Loading,
  MetricCard,
  Overline,
  PageHeader,
  SearchBox,
} from "./ui/BrandUI";

const PlatformMixCard = ({ platformMix }) => {
  const data = PLATFORMS.filter((p) => platformMix?.[p.key]).map((p) => ({ name: p.label, value: platformMix[p.key], color: p.color }));
  return (
    <Card className="flex min-w-[260px] flex-1 flex-col gap-2" padding="px-6 py-5">
      <Overline>Platform mix</Overline>
      {!data.length ? (
        <span className="bm-body-m text-bm-tertiary">No connected socials yet</span>
      ) : (
        <div className="flex items-center gap-4">
          <div className="h-28 w-28">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={0} outerRadius={50} stroke="white" strokeWidth={2} isAnimationActive={false}>
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${fmtCompact(value)} followers`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col gap-1">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 bm-body-m">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                <span className="text-bm-muted">{entry.name}</span>
                <span className="bm-label-l text-bm-ink">{fmtCompact(entry.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const analysisText = (items, stats) => {
  const unpriced = (stats.athleteCount || 0) - (stats.pricedCount || 0);
  return (
    `This shortlist packages to an estimated ${fmtMoneyCompact(stats.estMin)}–${fmtMoneyCompact(stats.estMax)} across all deliverables. ` +
    `You've priced ${stats.pricedCount || 0} of ${stats.athleteCount || 0} athletes at ${fmtMoney(stats.offersTotal || 0)} in offers` +
    (stats.targetBudget
      ? (stats.offersTotal || 0) <= stats.targetBudget
        ? `, ${fmtMoney(stats.targetBudget - (stats.offersTotal || 0))} under the ${fmtMoney(stats.targetBudget)} target`
        : `, ${fmtMoney((stats.offersTotal || 0) - stats.targetBudget)} over the ${fmtMoney(stats.targetBudget)} target`
      : "") +
    (unpriced > 0 ? `. ${unpriced} unpriced athlete${unpriced > 1 ? "s aren't" : " isn't"} counted yet, so committed cost will rise` : "") +
    `. The group reaches a combined ${fmtCompact(stats.combinedFollowers || 0)} followers at a ${stats.weightedEngagement || 0}% weighted engagement rate.`
  );
};

const ShortlistPage = () => {
  const navigate = useNavigate();
  const {
    shortlist,
    shortlistLoading,
    shortlistSaving,
    updateShortlistItem,
    removeFromShortlist,
    applyDealTemplate,
    setTargetBudget,
    addToShortlist,
    pool,
    createDraftFromShortlist,
  } = useBrandMode();
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [budgetInput, setBudgetInput] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const { items, stats, targetBudget } = shortlist;
  const editingItem = items.find((i) => i.athleteId === editingId) || null;

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return pool
      .filter((a) => !items.some((i) => i.athleteId === a.athleteId))
      .filter((a) => `${a.name} ${a.surname} ${a.school}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [search, pool, items]);

  const commitBudget = () => {
    if (budgetInput === null) return;
    setTargetBudget(Number(String(budgetInput).replace(/,/g, "")) || null);
    setBudgetInput(null);
  };

  const saveAsCampaign = async () => {
    setSaving(true);
    try {
      const created = await createDraftFromShortlist();
      toast.success("Draft campaign created from your shortlist.");
      navigate(`/brand/campaigns/${created.id}/edit`);
    } catch (err) {
      toast.error(err.message || "Could not create the campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Shortlist"
        subtitle="Package deliverables and model offers. Click a row to edit, then save it as a campaign to send offers."
        actions={
          <>
            <Btn variant="secondary" onClick={() => navigate("/brand/discover")}>Back to Discover</Btn>
            <Btn onClick={saveAsCampaign} disabled={!items.length || saving || shortlistSaving}>
              {saving ? "Saving..." : "Save as campaign"}
            </Btn>
          </>
        }
      />

      <div className="flex flex-col gap-4 pt-7">
        {shortlistLoading ? (
          <Loading label="Loading shortlist..." />
        ) : !items.length ? (
          <Card className="py-16 text-center">
            <p className="bm-body-m text-bm-muted">Your shortlist is empty.</p>
            <Btn className="mt-4" onClick={() => navigate("/brand/discover")}>Find athletes in Discover</Btn>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <MetricCard muted label="Athletes" value={stats?.athleteCount ?? items.length} sub="on this shortlist" />
              <MetricCard
                label="Estimated total"
                value={stats?.estMax ? `${fmtMoneyCompact(stats.estMin)}–${fmtMoneyCompact(stats.estMax)}` : EMPTY_VALUE}
                sub="across all deliverables"
              />
              <MetricCard label="Your offers total" value={fmtMoney(stats?.offersTotal || 0)} sub={`${stats?.pricedCount || 0} of ${stats?.athleteCount || 0} priced`} />
              <MetricCard label="Combined followers" value={fmtCompact(stats?.combinedFollowers || 0)} sub={`${stats?.weightedEngagement || 0}% weighted engagement`} />
              <MetricCard
                label="Budget vs target"
                value={targetBudget ? fmtMoney(targetBudget) : "No target"}
                sub={
                  targetBudget
                    ? (stats?.offersTotal || 0) <= targetBudget
                      ? `${fmtMoney(targetBudget - (stats?.offersTotal || 0))} under target`
                      : `${fmtMoney((stats?.offersTotal || 0) - targetBudget)} over target`
                    : "set one below"
                }
              />
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
              <InsightBox className="min-w-0 flex-1" title={`Shortlist analysis · ${stats?.athleteCount || items.length} athlete${(stats?.athleteCount || items.length) === 1 ? "" : "s"}`}>
                {analysisText(items, stats || {})}
              </InsightBox>
              <PlatformMixCard platformMix={stats?.platformMix} />
            </div>

            <Card className="flex flex-wrap items-center gap-4">
              <span className="bm-h-s text-bm-ink">Deal template</span>
              <Btn variant="tertiary" size="sm" onClick={() => setTemplateOpen(true)}>Apply to all</Btn>
              <span className="flex items-center gap-2 bm-body-m text-bm-muted">
                Target budget
                <span className="flex h-9 items-center gap-1 rounded-[8px] border border-bm-border px-3">
                  <span className="text-bm-muted">$</span>
                  <input
                    aria-label="Target budget"
                    value={budgetInput !== null ? budgetInput : targetBudget ? targetBudget.toLocaleString() : ""}
                    onChange={(e) => setBudgetInput(e.target.value.replace(/[^\d,]/g, ""))}
                    onBlur={commitBudget}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    placeholder="120,000"
                    inputMode="numeric"
                    className="bm-bare w-24 bm-label-l text-bm-ink"
                  />
                </span>
              </span>
              <div className="relative ml-auto w-full sm:w-[260px]">
                <SearchBox aria-label="Add athlete to shortlist" value={search} onChange={setSearch} placeholder="Add athlete by name or school" />
                {searchResults.length > 0 && (
                  <div className="absolute left-0 top-11 z-20 w-full overflow-hidden rounded-[12px] border border-bm-border bg-white shadow-xl">
                    {searchResults.map((a) => (
                      <button key={a.athleteId} type="button" onClick={() => { addToShortlist(a.athleteId); setSearch(""); }} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-bm-subtle">
                        <Avatar name={a.name} surname={a.surname} src={a.avatar} size={28} />
                        <span className="bm-label-l text-bm-ink">{fullName(a)}</span>
                        <span className="ml-auto bm-body-s text-bm-tertiary">{a.school}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card padding="p-0" className="overflow-x-auto">
              <table className="w-full min-w-[960px] border-collapse">
                <thead>
                  <tr>
                    <th className="bm-th">Athlete</th>
                    <th className="bm-th">Archetype</th>
                    <th className="bm-th !text-right">Score</th>
                    <th className="bm-th">CacheValue</th>
                    <th className="bm-th !text-right">Deliverables</th>
                    <th className="bm-th !text-right">Est. cost</th>
                    <th className="bm-th !text-right">Your offer</th>
                    <th className="bm-th">Offer</th>
                    <th className="bm-th" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const a = item.athlete;
                    const count = deliverableCount(item.deliverables);
                    const state = OFFER_RANGE[offerStatus(item.offer, item.estCost)];
                    return (
                      <tr key={item.athleteId} onClick={() => setEditingId(item.athleteId)} className="h-14 cursor-pointer hover:bg-bm-subtle">
                        <td className="bm-td">
                          <span className="flex items-center gap-[10px]">
                            <Avatar name={a.name} surname={a.surname} src={a.avatar} size={32} />
                            <span className="flex flex-col gap-[2px]">
                              <span className="bm-h-xs text-bm-ink">{fullName(a)}</span>
                              <span className="bm-body-s text-bm-muted">{[a.position, a.school].filter(Boolean).join(" · ")}</span>
                            </span>
                          </span>
                        </td>
                        <td className="bm-td">
                          <Badge tone="outline" dot={ARCHETYPE_DOT[a.archetype] || ARCHETYPE_DOT_FALLBACK}>{a.archetype}</Badge>
                        </td>
                        <td className="bm-td text-right bm-num-m text-bm-ink">{a.cacheScore}</td>
                        <td className="bm-td bm-body-m text-bm-ink">{fmtRange(a.cacheValue) || EMPTY_VALUE}</td>
                        <td className="bm-td text-right bm-body-m text-bm-ink">{count ? `${count} item${count > 1 ? "s" : ""}` : <span className="text-bm-tertiary">0 items</span>}</td>
                        <td className="bm-td text-right bm-body-m text-bm-ink">{item.estCost ? fmtRange(item.estCost, { compact: false }) : EMPTY_VALUE}</td>
                        <td className="bm-td text-right bm-label-l text-bm-ink">{item.offer ? fmtMoney(item.offer) : EMPTY_VALUE}</td>
                        <td className="bm-td">{state ? <Badge tone="outline" dot={state.dot}>{state.label}</Badge> : <span className="bm-body-m text-bm-tertiary">{EMPTY_VALUE}</span>}</td>
                        <td className="bm-td text-right">
                          <span className="flex items-center justify-end gap-3">
                            <button type="button" onClick={(e) => { e.stopPropagation(); setEditingId(item.athleteId); }} className="bm-label-xs text-bm-ink hover:text-bm-orange">Edit</button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); removeFromShortlist(item.athleteId); }} className="bm-label-xs text-bm-tertiary hover:text-bm-critical">Remove</button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </>
        )}

        <p className="bm-body-s text-bm-tertiary">
          Estimated costs are market ranges derived from each athlete's NILx rate card. Offers are yours to model, nothing here is a quote or a binding term.
        </p>
      </div>

      {templateOpen && (
        <DealTemplateModal
          athleteCount={items.length}
          onClose={() => setTemplateOpen(false)}
          onApply={async (mix) => {
            setTemplateOpen(false);
            await applyDealTemplate(mix);
            toast.success("Deal template applied to all athletes.");
          }}
        />
      )}
      {editingItem && (
        <AthleteOfferModal
          item={editingItem}
          onClose={() => setEditingId(null)}
          onSave={async (patch) => {
            setEditingId(null);
            await updateShortlistItem(editingItem.athleteId, patch);
          }}
          onRemove={() => {
            setEditingId(null);
            removeFromShortlist(editingItem.athleteId);
          }}
        />
      )}
    </div>
  );
};

export default ShortlistPage;
